// Resolves a pasted list of well names / APIs / UWIs to a camera extent and a
// matched-well set, by posting a THROWAWAY filter and reading its result — the
// only path that resolves name, API and UWI alike (WellListProcessor). Nothing
// here touches Redux, the map ref, or any store; the caller applies the result
// via setFlyToExtent / setJumpHighlightWells.
import * as Sentry from "@sentry/react";

import axios, { AxiosResponse } from "axios";
import { FlyToExtent } from "stores/map/mapTypes";
import { uid } from "utils/uid";

import { Result } from "api/dataSource";
import {
  FilterAbortHolder,
  WellListResult,
  generateFilterQuery,
  getFilterFromQuery,
  getWellsFromFilterIdPaginated
} from "api/filter";

import { EntityKind } from "models/entityKind";
import { toUnformattedIfUwi } from "models/uwi";

/** Redis TTL for the throwaway jump filter: long enough for the follow-up GET,
 *  short enough that nothing accumulates. */
export const JUMP_FILTER_EXPIRE_MINUTES = 5;

/** Ceiling on how many matched wells are handed to the highlight layer. The
 *  extent and the matched count are still computed over the FULL set
 *  server-side; only the highlight list is capped. */
export const JUMP_MAX_HIGHLIGHT_WELLS = 5000;

export type JumpToWellsFailure =
  | "empty" // nothing to submit — caller should not have called
  | "no-match" // resolved, but zero wells / no extent
  | "cancelled" // superseded by a newer submit → caller shows nothing
  | "failed"; // network or service error

export interface JumpToWellsSuccess {
  readonly extent: FlyToExtent;
  readonly uwis: string[]; // uniqueId, matches the tile `Uwi` property
  readonly matchedCount: number; // server totalCount over the full set
  readonly requestedCount: number; // parsed entries the user actually pasted
}

/** The wire shape WellListProcessor routes on — exactly these five fields.
 *  `pdenSource` and `negatedOperator` are deliberately omitted: the proven
 *  live path (filterSlice.ts filterWellsFromList) sends neither, and
 *  hardcoding a pdenSource would resolve the paste against the wrong pden
 *  source for any org not on the public one. */
interface WellListPredicate {
  readonly type: "WellList";
  readonly property: "Active";
  readonly operator: "in";
  readonly id: string;
  readonly value: readonly string[];
}

/**
 * Split, trim, drop empties, dedupe (case-insensitively), and unformat the
 * entries that are genuine UWIs.
 *
 * Separators are newline, carriage return and tab ONLY — never comma, never
 * space: well and Future Location stick names contain both, and splitting on
 * them shreds a name the entity-name lookup can never match again (EVA-6890).
 *
 * Uncapped — `parseWellEntries` is the capped entry point every real caller
 * should use.
 */
export function collectWellEntries(raw: string): string[] {
  const seen = new Set<string>();
  const entries: string[] = [];

  for (const rawEntry of raw.split(/[\n\r\t]+/)) {
    const trimmed = rawEntry.trim();
    if (!trimmed) continue;

    // Normalize FIRST, then dedupe on the normalized value: deduping on the raw
    // string let two spellings of one UWI both survive, so the server's count
    // came back lower and a full match was reported as partial.
    const normalized = toUnformattedIfUwi(trimmed);
    const dedupeKey = normalized.toLowerCase();
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    entries.push(normalized);
  }

  return entries;
}

/**
 * `collectWellEntries`, capped at `JUMP_MAX_HIGHLIGHT_WELLS` distinct entries:
 * an uncapped paste grows the POST body without limit.
 */
export function parseWellEntries(raw: string): string[] {
  return collectWellEntries(raw).slice(0, JUMP_MAX_HIGHLIGHT_WELLS);
}

/**
 * Creates a jump surface's OWN single-flight slot, and with it "latest submit
 * wins": a second submit aborts the first, which returns "cancelled".
 *
 * One per useWellJump instance, never module-level — several are live at once
 * (the Well tab, the Ctrl+A dialogue, a pair per map widget), and a shared
 * holder meant dismissing one surface silently aborted another's jump. Never
 * api/filter.ts's shared controller either: that belongs to the user's live
 * filter, and the two would cancel each other.
 *
 * suppressErrorToast because every failure here is reported in-panel.
 */
export function createJumpAbortHolder(): FilterAbortHolder {
  return { current: null, suppressErrorToast: true };
}

function reportFailure(stage: "post" | "read", entryCount: number): void {
  Sentry.withScope((scope) => {
    scope.setContext("map_jump_to_wells", { entryCount, stage });
    scope.setLevel("error");
    Sentry.captureException(new Error(`jumpToWells: ${stage} failed`));
  });
}

/** Aborts this holder's in-flight POST (and its follow-up GET via the same signal). */
export function abortPendingJump(holder: FilterAbortHolder): void {
  holder.current?.abort();
}

export async function jumpToWells(
  raw: string,
  abortHolder: FilterAbortHolder
): Promise<Result<JumpToWellsSuccess, JumpToWellsFailure>> {
  const entries = parseWellEntries(raw);
  if (entries.length === 0) {
    return { ok: false, error: "empty" };
  }

  const predicate: WellListPredicate = {
    type: "WellList",
    property: "Active",
    operator: "in",
    id: uid(),
    value: entries
  };

  const query = generateFilterQuery(
    [predicate],
    // polygon — query_processor.rs drops every polygon except source:"selection"
    // when a WellList predicate is present, so sending none is correct.
    null,
    [], // excludePolygons
    [], // properties
    // previousFilterId is ALWAYS "" — never the live filterId. Chaining it cuts
    // the user's session-long filter's TTL to 300s, and it evaporates mid-session.
    "",
    null, // screenshotPolygon
    EntityKind.Well,
    false // stampMapView — no polygon sent, nothing to stamp
  );
  // A fresh id per call. Belt-and-braces against data-svc-rs, which mints its
  // own uuid per request; load-bearing against eva-filter-svc, whose key is
  // user_id:job_id:entityKindInitial.
  query.job_id = uid();
  query.expireMinutes = JUMP_FILTER_EXPIRE_MINUTES;

  const postPromise = getFilterFromQuery(query, abortHolder);
  // Captured synchronously, BEFORE the await: getFilterFromQuery replaces
  // abortHolder.current on entry, so a newer submit would otherwise hand this
  // GET the newer call's signal.
  const signal = abortHolder.current?.signal;
  const postResult = await postPromise;
  // `Result.Ok.ok` is typed `boolean`, not `true`, so `!postResult.ok` alone
  // does not narrow away `Ok`.
  if (!postResult.ok && "error" in postResult) {
    if (postResult.error === "cancelled") {
      return { ok: false, error: "cancelled" };
    }
    reportFailure("post", entries.length);
    return { ok: false, error: "failed" };
  }

  // getWellsFromFilterIdPaginated returns bare `undefined` (not a rejected
  // promise) for a falsy filterId, so the catch below would never see it.
  const filterId = postResult.value;
  if (!filterId) {
    reportFailure("post", entries.length);
    return { ok: false, error: "failed" };
  }

  let response: Maybe<AxiosResponse<WellListResult>>;
  try {
    // The GET carries this call's own signal too: it has no single-flight guard,
    // so a superseded older submit could otherwise resolve last and win.
    response = await getWellsFromFilterIdPaginated(
      filterId,
      0,
      JUMP_MAX_HIGHLIGHT_WELLS,
      signal
    );
  } catch (err) {
    if (axios.isCancel(err)) {
      return { ok: false, error: "cancelled" };
    }
    reportFailure("read", entries.length);
    return { ok: false, error: "failed" };
  }

  const data = response?.data;
  const boundingBox = data?.boundingBox;
  // totalCount is optional on the wire and genuinely absent in the field
  // (WellList.tsx carries the same fallback). Defaulting it to 0 would send a
  // response that did resolve into the no-match branch below.
  const totalCount = data?.totalCount ?? data?.uwiList?.length ?? 0;
  const bounds = boundingBox
    ? [boundingBox.minx, boundingBox.miny, boundingBox.maxx, boundingBox.maxy]
    : [];

  // A non-finite bound throws inside fitBounds and breaks the map.
  if (boundingBox == null || totalCount === 0 || !bounds.every(Number.isFinite)) {
    return { ok: false, error: "no-match" };
  }

  const uwis = (data.uwiList ?? []).map((well) => well.uniqueId);

  return {
    ok: true,
    value: {
      extent: {
        minx: boundingBox.minx,
        miny: boundingBox.miny,
        maxx: boundingBox.maxx,
        maxy: boundingBox.maxy
      },
      uwis,
      matchedCount: totalCount,
      requestedCount: entries.length
    }
  };
}
